import Cookies from 'js-cookie'
import { AUTH_COOKIE } from './Constants'
import { loginRoute } from './loginUrl'

const REJECTED_KEY = 'comms-v2.rejected-session'

/**
 * A short tag for one cookie: its last 32 characters, which encode the end
 * of its HMAC. Enough to tell sessions apart without storing the credential.
 */
export const sessionFingerprint = (raw) => (raw ? raw.slice(-32) : null)

const storage = () => {
    try {
        return window.sessionStorage
    } catch {
        return null
    }
}

/**
 * Remember that the API rejected this cookie (401), for this tab.
 *
 * The cookie may still decode as fresh on the client (expired server-side,
 * bad signature), and deleting it depends on matching its domain. Marking it
 * instead means the gate treats it as signed out no matter what, so a 401
 * can never bounce between a page and the sign-in screen. A new login sets
 * a new cookie with a new fingerprint, which is not marked.
 */
export const markSessionRejected = (raw) => {
    const fingerprint = sessionFingerprint(raw)
    try {
        if (fingerprint) storage()?.setItem(REJECTED_KEY, fingerprint)
    } catch {
        // Storage blocked (private mode, sandbox): the redirect still happens.
    }
}

/** True when this exact cookie was rejected by the API in this tab. */
export const isSessionRejected = (raw) => {
    const fingerprint = sessionFingerprint(raw)
    try {
        return !!fingerprint && storage()?.getItem(REJECTED_KEY) === fingerprint
    } catch {
        return false
    }
}

let redirecting = false

/** Test hook: allow another redirect. */
export const resetUnauthorizedRedirect = () => {
    redirecting = false
}

/**
 * The API answered 401: the session is over. Mark the cookie rejected and
 * send the user to the sign-in screen, remembering the page they were on.
 * Several requests usually fail together, so only the first one redirects;
 * on the sign-in screen itself nothing happens.
 *
 * @param {object} [deps]  injectable for tests
 * @returns {string|null} the path navigated to, or null
 */
export const handleUnauthorized = ({
    cookie = Cookies.get(AUTH_COOKIE),
    location = window.location,
    basePath = process.env.PUBLIC_URL || '',
    assign = (url) => window.location.assign(url),
} = {}) => {
    markSessionRejected(cookie)
    if (redirecting) return null
    const routerPath =
        basePath && location.pathname.startsWith(basePath)
            ? location.pathname.slice(basePath.length) || '/'
            : location.pathname
    if (routerPath === '/login') return null
    const to = `${basePath}${loginRoute(routerPath, location.search)}`
    redirecting = true
    assign(to)
    return to
}

/**
 * The cookies a sign-out removes: the admin session and the three parts of
 * the CloudFront signed cookie, as the applause apps' logout does
 * (applause packages/utils/src/logout.ts).
 */
export const SESSION_COOKIES = [
    AUTH_COOKIE,
    'CloudFront-Policy',
    'CloudFront-Signature',
    'CloudFront-Key-Pair-Id',
]

/**
 * Every Domain a cookie seen on `hostname` may have been set with: none
 * (host-only), the host itself, and each parent domain short of the
 * top-level one (comms.dev.bhanzu.com: .dev.bhanzu.com, .bhanzu.com).
 *
 * @param {string} hostname
 * @returns {(string|undefined)[]} undefined stands for host-only
 */
export const cookieDomains = (hostname) => {
    const labels = hostname ? hostname.split('.') : []
    const domains = [undefined, hostname || undefined]
    for (let i = 1; i < labels.length - 1; i += 1)
        domains.push(`.${labels.slice(i).join('.')}`)
    return [...new Set(domains)]
}

/**
 * Sign out on this browser: delete every session cookie on the host and on
 * every parent domain.
 *
 * A cookie can only be deleted with the Domain it was set with, and the
 * admin cookie may sit on .bhanzu.com (production) as well as
 * .dev.bhanzu.com (dev) or the host alone. Deleting one copy on one domain
 * left the other in place: on dev with a production cookie present the user
 * still read as signed in, and the sign-in page sent them straight back.
 *
 * @param {object} [deps]  injectable for tests
 * @returns {string[]} the cookie strings written
 */
export const clearSession = ({
    hostname = window.location.hostname,
    write = (cookie) => {
        document.cookie = cookie
    },
} = {}) => {
    const past = 'Thu, 01 Jan 1970 00:00:01 GMT'
    const written = SESSION_COOKIES.flatMap((name) =>
        cookieDomains(hostname).map(
            (domain) =>
                `${name}=; Path=/; Expires=${past};${
                    domain ? ` Domain=${domain};` : ''
                }`
        )
    )
    written.forEach(write)
    return written
}
